import React, {
  Suspense,
  forwardRef,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {useController} from 'react-hook-form';
import {mergeProps, useObjectRef} from '@react-aria/utils';
import {mergeRefs} from 'react-merge-refs';
import {BaseFieldPropsWithDom} from '../base-field-props';
import {getInputFieldClassNames} from '../get-input-field-class-names';
import {Field} from '../field';
import {useField} from '../use-field';
import 'intl-tel-input/build/css/intlTelInput.css';
import './phone-field.css';
import {IntlTelInputRef} from 'intl-tel-input/react';
import type IntlTelInputComponent from 'intl-tel-input/reactWithUtils';
import {message} from '@common/i18n/message';
import {useTrans} from '@common/i18n/use-trans';
import clsx from 'clsx';

const errorMap = [
  'Invalid number',
  'Invalid country code',
  'Too short',
  'Too long',
  'Invalid number',
];

const IntlTelInput = lazy(
  () => import('intl-tel-input/reactWithUtils'),
) as typeof IntlTelInputComponent;
export interface PhoneFieldProps
  extends BaseFieldPropsWithDom<HTMLInputElement> {
  inputRef?: React.Ref<HTMLInputElement>;
  value?: string;
  onChange?: (value: any) => void;
  onChangeValidity?: (value: any) => void;
  onChangeErrorCode?: (value: any) => void;
  onlyCountries?: string[];
  preferredCountries?: string[];
  excludeCountries?: string[];
  initialCountry?: string;
}

export const PhoneField = forwardRef<HTMLDivElement, PhoneFieldProps>(
  (
    {
      inputRef,
      inputTestId,
      value,
      onChange,
      onChangeValidity,
      onChangeErrorCode,
      onlyCountries = [],
      preferredCountries,
      excludeCountries = [],
      initialCountry = 'auto',
      ...props
    },
    ref,
  ) => {
    const inputObjRef = useObjectRef(inputRef);
    const {fieldProps, inputProps} = useField<HTMLInputElement>({
      ...props,
      focusRef: inputObjRef,
    });

    const inputFieldClassNames = getInputFieldClassNames({
      ...props,
    });
    const phoneInputClassName = clsx(
      'phone-field-ltr-input',
      '!h-auto !rounded-[10px] !border-[1.5px] !py-12 !shadow-none focus:!ring-0',
      props.invalid
        ? '!border-danger !bg-danger/4 !font-bold !text-danger focus:!border-danger !shadow-[0_0_0_3px_rgb(239_68_68_/_0.10)]'
        : '!border-divider !bg-paper !text-muted focus:!border-divider',
    );
    inputProps.className = clsx(
      'w-full',
      inputFieldClassNames.input,
      phoneInputClassName,
    );
    inputFieldClassNames.wrapper = clsx(
      inputFieldClassNames.wrapper,
      'phone-field-ltr',
      props.invalid
        ? '[&_.iti__selected-country-primary]:border-danger-lighter'
        : '[&_.iti__selected-country-primary]:border-divider',
      '[&_.iti__arrow]:!m-0 [&_.iti__arrow]:!border-t-text-muted',
      '[&_.iti__flag]:!h-[15px] [&_.iti__flag]:!w-[22px] [&_.iti__flag]:!rounded-2',
    );
    const itiRef = useRef<IntlTelInputRef>(null);

    const handleNumberChange = (e: string) => {
      if (itiRef.current) {
        const instance = itiRef.current.getInstance();
        const formattedNumber = instance?.getNumber();
        // Ensure we're passing the formatted number to onChange
        if (formattedNumber) {
          onChange?.(formattedNumber);
        } else {
          // If formatting fails, preserve the visible input value.
          onChange?.(itiRef.current.getInput()?.value || e);
        }
      }
    };

    const syncInputValue = useCallback(() => {
      const input = itiRef.current?.getInput() || inputObjRef.current;
      const nextValue = value == null ? '' : value;
      const instance = itiRef.current?.getInstance();

      if (input && inputObjRef.current !== input) {
        inputObjRef.current = input;
      }

      if (!nextValue && (input?.value || instance?.getNumber())) {
        return;
      }

      if (
        instance &&
        (instance.getNumber() !== nextValue || nextValue.startsWith('+'))
      ) {
        instance.setNumber(nextValue);
      }

      if (input && !nextValue.startsWith('+') && input.value !== nextValue) {
        input.value = nextValue;
      }
    }, [inputObjRef, value]);

    useEffect(() => {
      syncInputValue();

      const timeout = window.setTimeout(syncInputValue);
      itiRef.current?.getInstance()?.promise.then(syncInputValue);

      return () => window.clearTimeout(timeout);
    }, [syncInputValue]);

    return (
      <Field ref={ref} fieldClassNames={inputFieldClassNames} {...fieldProps}>
        <Suspense
          fallback={
            <input
              {...(inputProps as any)}
              aria-busy="true"
              disabled
              type="tel"
              value={value || ''}
              readOnly
            />
          }
        >
          <IntlTelInput
            ref={itiRef}
            initialValue={value}
            inputProps={inputProps as any}
            initOptions={{
              onlyCountries,
              initialCountry,
              excludeCountries,
            }}
            onChangeNumber={handleNumberChange}
            onChangeValidity={onChangeValidity}
            onChangeErrorCode={onChangeErrorCode}
          />
        </Suspense>
      </Field>
    );
  },
);

export interface FormPhoneFieldProps extends PhoneFieldProps {
  name: string;
  hasError?: boolean;
}

export const FormPhoneField = forwardRef<HTMLDivElement, FormPhoneFieldProps>(
  ({name, inputRef: externalInputRef, hasError: _hasError, ...props}, ref) => {
    const [isValid, setIsValid] = useState(true);
    const [errorCode, setErrorCode] = useState<number | null>(null);
    const latestValueRef = useRef<string>('');
    const {trans} = useTrans();

    const {
      field: {onChange, onBlur, value = '', ref: inputRef},
      fieldState: {invalid, error, isTouched},
      formState: {isSubmitting},
    } = useController({
      name,
      rules: {
        validate: {
          phoneValid: value => {
            // Return true only if the number is valid
            if (!isValid) {
              return trans(message?.(errorMap[errorCode || 0]));
            }
            // Also validate that we have a value
            if (!latestValueRef.current?.trim()) {
              return 'Phone number is required';
            }
            return true;
          },
        },
      },
    });

    useEffect(() => {
      latestValueRef.current = value == null ? '' : value;
    }, [value]);

    const handleValidationChange = (
      isNewValid: boolean | undefined,
      errorCode: number | null | undefined,
    ) => {
      if (isNewValid !== undefined) setIsValid(isNewValid || !isTouched);
      if (errorCode !== undefined) setErrorCode(!isTouched ? null : errorCode);

      if (!isSubmitting) {
        // Use the latest value when triggering validation
        onChange(latestValueRef.current);
      }
    };

    const formProps: PhoneFieldProps = {
      onChange: (phoneNumber: string) => {
        // Store the latest value
        latestValueRef.current = phoneNumber;
        onChange(phoneNumber);
      },
      onBlur,
      value: value == null ? '' : value,
      invalid: invalid || !isValid,
      errorMessage:
        error?.message ||
        (errorCode !== null ? trans(message(errorMap[errorCode])) : undefined),
      inputRef: externalInputRef
        ? mergeRefs([inputRef, externalInputRef])
        : inputRef,
      name,
      onChangeValidity: (isValid: boolean) => {
        handleValidationChange(isValid, undefined);
      },
      onChangeErrorCode: (errorCode: number | null) => {
        handleValidationChange(undefined, errorCode);
      },
    };

    return <PhoneField ref={ref} {...mergeProps(formProps, props)} />;
  },
);
